import { AuthenticatedContext, User } from '@sec-loans-ui/common/types'
import {
  createWizardMachine,
  shouldHydrateState,
  WizardContext,
  WizardMachineType,
  WizardStep,
} from '@sec-loans-ui/common/wizard/rootWizardMachine'
import { SecLoansAnalyticsService } from '@sec-loans-ui/services/analyticsService'
import { useAnalyticsService } from '@sec-loans-ui/state/analyticsContext'
import { getSecLoansOpenAbortModalEvent } from '@sec-loans-ui/wizard/Wizard'
import { OpenModalEvent } from '@ui-common/components/Modal'
import { usePersistingActor } from '@ui-common/hooks'
import { GlobalServicesContext } from '@ui-common/state'
import { commonExit, getLanguage } from '@ui-common/utils'
import { shallowEqual, useSelector } from '@xstate/react'
import { use, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { ActorRefFromLogic, AnyActorRef, SnapshotFrom } from 'xstate'

import { getExitUrl, getFallbackExitUrls } from '../../utils/helpers'

export interface RootWizardLogic {
  analyticsService: SecLoansAnalyticsService
  step: WizardStep
  user?: User
  authToken?: string
  secLoansEntryContext: WizardContext['secLoansEntryContext']
  ifpLoansEntryContext: WizardContext['ifpLoansEntryContext']
  loanOfferEntryContext: WizardContext['loanOfferEntryContext']
  entranceStepMachineActor?: AnyActorRef
  send: ActorRefFromLogic<WizardMachineType>['send']
  onAbort: () => void
  showAbortPromptModal: () => void
}

const selectValues = ({
  context: { user, secLoansEntryContext, ifpLoansEntryContext, loanOfferEntryContext, authToken, exitUrls },
  children,
  value,
}: SnapshotFrom<WizardMachineType>) => ({
  user,
  secLoansEntryContext,
  ifpLoansEntryContext,
  loanOfferEntryContext,
  authToken,
  exitUrls,
  children,
  step: value,
})

/**
 * Not to be used outside RootWizard
 */
export const useWizardLogic = (authenticatedContext?: AuthenticatedContext): RootWizardLogic => {
  const intl = useIntl()

  const { modalService } = use(GlobalServicesContext)
  const modalSend = modalService.send

  const wizardMachine = createWizardMachine(authenticatedContext)
  const actor = usePersistingActor<WizardMachineType>(wizardMachine, shouldHydrateState)

  const {
    step,
    user,
    secLoansEntryContext,
    ifpLoansEntryContext,
    loanOfferEntryContext,
    authToken,
    exitUrls,
    children,
  } = useSelector(actor, selectValues, shallowEqual)

  const entranceStepMachineActor = children['entranceStepMachine']

  const onAbort = () => {
    actor.send({ type: 'EXIT_WIZARD' })
  }

  useEffect(() => {
    void (async () => {
      if (step === WizardStep.Aborted) {
        const language = getLanguage()
        const urls = exitUrls ?? getFallbackExitUrls()
        const baseUrl = urls.failureUrl[language]
        const url = await getExitUrl(baseUrl, urls.cbsTokenParameter)
        await commonExit(url)
      }
    })()
  }, [step, exitUrls])

  const { analyticsService } = useAnalyticsService()
  const showAbortPromptModal = () => {
    analyticsService.sendModalViewEvent('AbortModal')
    const openEvent: OpenModalEvent = getSecLoansOpenAbortModalEvent({
      onAbort: () => {
        analyticsService.sendCustomEvent('AbortModal', 'ExitButton')
        onAbort()
      },
      analyticsService,
      analyticsCategory: 'AbortModal',
      intl,
    })
    modalSend(openEvent)
  }

  return {
    analyticsService,
    step,
    secLoansEntryContext,
    ifpLoansEntryContext,
    loanOfferEntryContext,
    user,
    authToken,
    entranceStepMachineActor,
    send: actor.send,
    onAbort,
    showAbortPromptModal,
  }
}
